import { useCallback, useMemo } from 'react';
import { usePaginatedDropdown, PaginatedRequest } from './usePaginatedDropdown';
import {
  GetGroupedLocationsWithPagination,
  GetProducts,
} from '@/apis/onboarding';
import type { PaginatedResponse } from './usePaginatedDropdown';

export interface FilterConfig {
  type: number;
  key: string;
  label: string;
  fetcher?: (params: PaginatedRequest) => Promise<PaginatedResponse<unknown>>;
  usePagination?: boolean;
  filterType?: string;
}

export interface UsePaginatedFiltersResult {
  filters: Record<
    number,
    {
      data: unknown[];
      loading: boolean;
      hasMore: boolean;
      loadMore: () => void;
      search: string;
      setSearch: (search: string) => void;
    }
  >;
  getFilterData: (type: number) => unknown[];
  getFilterLoading: (type: number) => boolean;
  getFilterHasMore: (type: number) => boolean;
  getFilterLoadMore: (type: number) => () => void;
}

export const usePaginatedFilters = (
  configs: FilterConfig[],
  getToken: () => string | null
): UsePaginatedFiltersResult => {
  // Memoize the fetchers to prevent infinite re-renders
  const fetchers = useMemo(() => {
    return configs.reduce(
      (acc, config) => {
        acc[config.type] = async ({
          page,
          limit,
          search,
        }: PaginatedRequest) => {
          const token = getToken();
          if (!token) return { data: [], total: 0, hasNext: false };

          try {
            let response;
            let responseData;

            switch (config.type) {
              case 3: // Origins
                response = await GetGroupedLocationsWithPagination(
                  token,
                  page,
                  limit || 20,
                  search,
                  config.filterType || 'origin'
                );
                responseData = response?.data?.data;
                if (
                  responseData &&
                  responseData.data &&
                  responseData.data.items
                ) {
                  return {
                    data: responseData.data.items,
                    total: responseData.data.pagination?.total_items || 0,
                    hasNext: responseData.data.pagination?.has_next || false,
                  };
                }
                break;

              case 4: // Destinations
                response = await GetGroupedLocationsWithPagination(
                  token,
                  page,
                  limit || 20,
                  search,
                  config.filterType || 'destination'
                );
                responseData = response?.data?.data;
                if (
                  responseData &&
                  responseData.data &&
                  responseData.data.items
                ) {
                  return {
                    data: responseData.data.items,
                    total: responseData.data.pagination?.total_items || 0,
                    hasNext: responseData.data.pagination?.has_next || false,
                  };
                }
                break;

              case 1: // Products
                response = await GetProducts(token, page, search);
                responseData = response?.data?.data;
                if (
                  responseData?.is_success &&
                  Array.isArray(responseData?.data)
                ) {
                  return {
                    data: responseData.data,
                    total:
                      responseData.requirement_count ||
                      responseData.data.length,
                    hasNext: false, // Products API doesn't seem to have pagination info
                  };
                }
                break;

              default:
                // For non-paginated filters, use the custom fetcher
                if (config.fetcher) {
                  return await config.fetcher({ page, limit, search });
                }
            }

            return { data: [], total: 0, hasNext: false };
          } catch (error) {
            console.error(`Error fetching ${config.label}:`, error);
            return { data: [], total: 0, hasNext: false };
          }
        };
        return acc;
      },
      {} as Record<
        number,
        (params: PaginatedRequest) => Promise<PaginatedResponse<unknown>>
      >
    );
  }, [configs, getToken]);

  // Call hooks individually (not in a loop) to follow React rules
  const originsHook = usePaginatedDropdown({
    fetchFn: fetchers[3],
    initialLimit: 50,
    filterType: configs.find((c) => c.type === 3)?.filterType,
  });

  const productsHook = usePaginatedDropdown({
    fetchFn: fetchers[1],
    initialLimit: 50,
    filterType: configs.find((c) => c.type === 1)?.filterType,
  });

  const destinationsHook = usePaginatedDropdown({
    fetchFn: fetchers[4],
    initialLimit: 50,
    filterType: configs.find((c) => c.type === 4)?.filterType,
  });

  // Create filters object from individual hooks
  const filters = useMemo(() => {
    const result: Record<
      number,
      {
        data: unknown[];
        loading: boolean;
        hasMore: boolean;
        loadMore: () => void;
        search: string;
        setSearch: (search: string) => void;
      }
    > = {};

    configs.forEach((config) => {
      if (config.usePagination !== false) {
        let hook;
        switch (config.type) {
          case 1: // Products
            hook = productsHook;
            break;
          case 3: // Origins
            hook = originsHook;
            break;
          case 4: // Destinations
            hook = destinationsHook;
            break;
          default:
            return;
        }

        result[config.type] = {
          data: hook.data,
          loading: hook.loading,
          hasMore: hook.hasMore,
          loadMore: hook.loadMore,
          search: hook.search,
          setSearch: hook.setSearch,
        };
      }
    });

    return result;
  }, [configs, originsHook, productsHook, destinationsHook]);

  // Helper functions - memoized to prevent re-renders
  const getFilterData = useCallback(
    (type: number) => filters[type]?.data || [],
    [filters]
  );
  const getFilterLoading = useCallback(
    (type: number) => filters[type]?.loading || false,
    [filters]
  );
  const getFilterHasMore = useCallback(
    (type: number) => filters[type]?.hasMore || false,
    [filters]
  );
  const getFilterLoadMore = useCallback(
    (type: number) => filters[type]?.loadMore || (() => {}),
    [filters]
  );

  return {
    filters,
    getFilterData,
    getFilterLoading,
    getFilterHasMore,
    getFilterLoadMore,
  };
};
